import { useEffect, useState } from "react";
import { Typography, Box, Paper } from "src/components";
import { getContentsIcon } from 'src/data'; 
import { useIsMobile } from 'src/hooks';

import TableOfContentsInline from './table-of-contents-inline';
import RelatedArticles from './related-articles';
import Markdown from './markdown';
import ArticleOverlayControlButtons from './article-overlay-control-buttons';
import CommentsContainer from '../comments/comments-container';
import ReportIssue from './report-issue';

export default function HandbookArticle({ id, title, markdown, relatedArticles }) {
  const [tableOfContents, setTableOfContents] = useState([]);
  const [showControls, setShowControls] = useState(false);

  const cleanedContent = markdown?.replace(/^##\s+\[[^\]]+\]\s+/gm, '## ');
  const isMobile = useIsMobile();

  const isSidebarEnabled = import.meta.env.VITE_FEATURE_HANDBOOK_SIDENAV === 'true';

  useEffect(() => {
    extractTableOfContents(markdown);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const shouldShow = window.scrollY > 100; 
      setShowControls(shouldShow);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const extractTableOfContents = (content) => {
    if (!content || typeof content !== 'string') {
      setTableOfContents([]);
      return;
    }

    const headings = content.match(/^#{1,3}\s+(.+)$/gm);
    if (!headings) {
      setTableOfContents([]);
      return;
    }

    const toc = headings
      .map((heading, index) => {
        const level = heading.match(/^#+/)[0].length;
        if (level !== 2) return null;

        let rawText = heading.replace(/^##\s+/, '').trim();

        let icon = getContentsIcon('default');
        let text = rawText;

        const iconMatch = rawText.match(/^\[(.+?)\]\s*(.*)/);
        if (iconMatch) {
          const iconKey = iconMatch[1];
          text = iconMatch[2].trim();
          icon = getContentsIcon(iconKey);
        }

        const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

        return {
          level,
          text,
          id,
          icon,
          index,
        };
      })
      .filter(Boolean);

    setTableOfContents(toc);
  };

  return (<>
    <Box sx={{ pt: 2, pb: 4, px: isMobile ? 0 : 3, width: '100%', maxWidth: '1100px' }} >

      <Box display="flex" gap={3} sx={{width: '100%'}}>
        {/* Main Content */}
        <Paper elevation={0} sx={{ p: isMobile ? 2 : 6, borderRadius: 2, width: !isMobile && isSidebarEnabled ? '75%' : '100%' }}>
            <Typography variant={isMobile ? "h3" : "h2"} component="h1" sx={{ mb: 2 }}>
              {title}
            </Typography>

            {tableOfContents.length > 0 ? (
              <TableOfContentsInline tableOfContents={tableOfContents}/>
            ):<></>}

            <Markdown children={cleanedContent}/>

            <Box sx={{mt: 3}}>
              <ReportIssue articleId={id} />
            </Box>
          </Paper>

        {/* Sidebar */}
        {!isMobile && isSidebarEnabled && (
          <Box sx={{ borderRadius: 2, width: '25%', display: { xs: 'none', md: 'block' }, bgcolor: '#fff' }}>

          </Box>
        )}
        
          
      </Box>

      <Box sx={{px: 2}}>
        <CommentsContainer articleId={id} />
      </Box>

    </Box>

    {relatedArticles.length > 0 ? (
        <RelatedArticles relatedArticles={relatedArticles}/>
      ): <></>}

    <ArticleOverlayControlButtons
        articleId={id}
        tableOfContents={tableOfContents}
        visible={showControls}
      />

    </>
  );
}